import { defineStore } from 'pinia';
import { ref } from 'vue';

/**
 * Parse a single CSV line respecting quoted fields (handles commas inside quotes).
 */
function parseCSVLine(line) {
  const result = [];
  let i = 0;
  while (i < line.length) {
    if (line[i] === '"') {
      i += 1;
      let field = '';
      while (i < line.length) {
        if (line[i] === '"') {
          if (line[i + 1] === '"') {
            field += '"';
            i += 2;
          } else {
            i += 1;
            break;
          }
        } else {
          field += line[i];
          i += 1;
        }
      }
      result.push(field);
      if (line[i] === ',') i += 1;
    } else {
      let field = '';
      while (i < line.length && line[i] !== ',') {
        field += line[i];
        i += 1;
      }
      result.push(field.trim());
      if (line[i] === ',') i += 1;
    }
  }
  return result;
}

/**
 * Parse CSV text into rows of columns. First row is treated as header.
 */
function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((line) => line.length > 0);
  if (lines.length === 0) return { header: [], rows: [] };
  const header = parseCSVLine(lines[0]);
  const rows = lines.slice(1).map((line) => parseCSVLine(line));
  return { header, rows };
}

function colIndex(header, patterns, fallback) {
  const i = header.findIndex((h) => patterns.some((re) => re.test(String(h || '').trim())));
  return i >= 0 ? i : fallback;
}

/* eslint-disable import/prefer-default-export */
export const useProvidersStore = defineStore('providers', () => {
  const providers = ref([]);
  const loading = ref(false);

  async function fetchProviders() {
    loading.value = true;
    try {
      const res = await fetch('/providers.csv');
      if (!res.ok) throw new Error(`Failed to load CSV: ${res.status}`);
      const text = await res.text();
      const { header, rows } = parseCSV(text);

      const ni = colIndex(header, [/^PROVIDER$/i], 0);
      const li = colIndex(header, [/^PHYSICAL_LOC$/i], 1);
      const countyI = colIndex(header, [/^COUNTY$/i], 2);
      const panelI = colIndex(header, [/^PANEL_ACCESS$/i], 3);
      // Legacy CSV fallback: TEL_NO as contact when panel/county missing
      const contactI = colIndex(header, [/^TEL_NO$/i, /^CONTACT$/i], -1);

      providers.value = rows.map((row, index) => ({
        id: `row-${index + 1}`,
        name: (row[ni] ?? '').trim() || undefined,
        location: (row[li] ?? '').trim() || undefined,
        county: (row[countyI] ?? '').trim() || undefined,
        panelAccess: (row[panelI] ?? '').trim() || undefined,
        contact: contactI >= 0 ? ((row[contactI] ?? '').trim() || undefined) : undefined,
      }));
    } catch (e) {
      providers.value = [];
    } finally {
      loading.value = false;
    }
  }

  return { providers, loading, fetchProviders };
});
